import { NOTIFICATION_CENTER_TAB_IDS } from "./notificationCenterConfig";
import {
  NOTIFICATION_CENTER_DEADLINES_SCOPE,
  NOTIFICATION_CENTER_DECLARATION_TYPE,
  NOTIFICATION_CENTER_VISIBILITY,
} from "../utils/notificationCenterFilterConstants";
import {
  getNotificationCenterRegistrationsDefaultStatuses,
  normalizeRegistrationsCompanyFilter,
  normalizeRegistrationsJurisdictionFilter,
  normalizeRegistrationsOrderDateFilter,
  normalizeRegistrationsOrdering,
} from "../utils/notificationCenterRegistrationsFilters";

/** @typedef {'select'} NotificationCenterFilterType */

/**
 * @typedef {Object} NotificationCenterFilterOptionDef
 * @property {string} value
 * @property {string} labelKey
 */

/**
 * @typedef {Object} NotificationCenterFilterDef
 * @property {string} id
 * @property {NotificationCenterFilterType} type
 * @property {string} reduxKey
 * @property {string} placeholderKey
 * @property {string} ariaLabelKey
 * @property {NotificationCenterFilterOptionDef[]} options
 * @property {'labeled' | null} [variant]
 * @property {string} [labelPrefixKey]
 */

export const NOTIFICATION_CENTER_FILTER_IDS = Object.freeze({
  VISIBILITY: "visibility",
  DEADLINES_SCOPE: "deadlinesScope",
  DECLARATION_TYPE: "declarationType",
  REGISTRATIONS_COMPANY: "registrationsCompany",
  REGISTRATIONS_ORDER_DATE: "registrationsOrderDate",
  REGISTRATIONS_JURISDICTION: "registrationsJurisdiction",
  REGISTRATIONS_TYPE_OF_REGISTRATION: "registrationsTypeOfRegistration",
  REGISTRATIONS_SUBCATEGORY: "registrationsSubcategory",
  REGISTRATIONS_STATUS: "registrationsStatus",
  REGISTRATIONS_DATE_LAST_STATUS_CHANGED:
    "registrationsDateLastStatusChanged",
  REGISTRATIONS_REGISTRATOR: "registrationsRegistrator",
  REGISTRATIONS_ESTIMATED_REGISTRATION_DATE:
    "registrationsEstimatedRegistrationDate",
});

const VISIBILITY_FILTER = Object.freeze({
  id: NOTIFICATION_CENTER_FILTER_IDS.VISIBILITY,
  type: "select",
  reduxKey: "visibilityFilter",
  placeholderKey: "filters.visibility.mine",
  ariaLabelKey: "filters.visibility.mine",
  options: [
    {
      value: NOTIFICATION_CENTER_VISIBILITY.MINE,
      labelKey: "filters.visibility.mine",
    },
    {
      value: NOTIFICATION_CENTER_VISIBILITY.ALL,
      labelKey: "filters.visibility.all",
    },
  ],
});

const DEADLINES_SCOPE_FILTER = Object.freeze({
  id: NOTIFICATION_CENTER_FILTER_IDS.DEADLINES_SCOPE,
  type: "select",
  reduxKey: "deadlinesScopeFilter",
  variant: "labeled",
  labelPrefixKey: "filters.deadlinesPrefix",
  placeholderKey: "filters.deadlines.next3Days",
  ariaLabelKey: "filters.deadlines.next3Days",
  options: [
    {
      value: NOTIFICATION_CENTER_DEADLINES_SCOPE.NEXT_3_DAYS,
      labelKey: "filters.deadlines.next3Days",
    },
    {
      value: NOTIFICATION_CENTER_DEADLINES_SCOPE.FORTHCOMING,
      labelKey: "filters.deadlines.forthcoming",
    },
    {
      value: NOTIFICATION_CENTER_DEADLINES_SCOPE.ALL,
      labelKey: "filters.deadlines.all",
    },
    {
      value: NOTIFICATION_CENTER_DEADLINES_SCOPE.MISSED,
      labelKey: "filters.deadlines.missed",
    },
  ],
});

const DECLARATION_TYPE_FILTER = Object.freeze({
  id: NOTIFICATION_CENTER_FILTER_IDS.DECLARATION_TYPE,
  type: "select",
  reduxKey: "declarationTypeFilter",
  variant: "labeled",
  labelPrefixKey: "filters.declarationTypePrefix",
  placeholderKey: "filters.declarationType.all",
  ariaLabelKey: "filters.declarationType.all",
  options: [
    {
      value: NOTIFICATION_CENTER_DECLARATION_TYPE.ALL,
      labelKey: "filters.declarationType.all",
    },
    {
      value: NOTIFICATION_CENTER_DECLARATION_TYPE.DECLARATION,
      labelKey: "filters.declarationType.declaration",
    },
    {
      value: NOTIFICATION_CENTER_DECLARATION_TYPE.SALES_TAX_RETURN,
      labelKey: "filters.declarationType.salesTaxReturn",
    },
    {
      value: NOTIFICATION_CENTER_DECLARATION_TYPE.EPR_REPORT,
      labelKey: "filters.declarationType.eprReport",
    },
  ],
});

/** Options come from GET /registration_tracker/references. */
const REGISTRATIONS_COMPANY_FILTER = Object.freeze({
  id: NOTIFICATION_CENTER_FILTER_IDS.REGISTRATIONS_COMPANY,
  type: "select",
  optionsSource: "companies",
  reduxKey: "registrationsCompanyFilter",
  variant: "labeled",
  labelPrefixKey: "filters.registrations.companyPrefix",
  placeholderKey: "filters.registrations.allCompanies",
  ariaLabelKey: "filters.registrations.company",
  allOptionLabelKey: "filters.registrations.allCompanies",
  options: [],
});

const REGISTRATIONS_ORDER_DATE_FILTER = Object.freeze({
  id: NOTIFICATION_CENTER_FILTER_IDS.REGISTRATIONS_ORDER_DATE,
  type: "dateRange",
  reduxKey: "registrationsOrderDateFilter",
  placeholderKey: "filters.registrations.orderDate",
  ariaLabelKey: "filters.registrations.orderDate",
  labelPrefixKey: "filters.registrations.orderDatePrefix",
  options: [],
});

const REGISTRATIONS_JURISDICTION_FILTER = Object.freeze({
  id: NOTIFICATION_CENTER_FILTER_IDS.REGISTRATIONS_JURISDICTION,
  type: "multiSelect",
  optionsSource: "jurisdictions",
  reduxKey: "registrationsJurisdictionFilter",
  placeholderKey: "filters.registrations.allJurisdictions",
  ariaLabelKey: "filters.registrations.jurisdiction",
  labelPrefixKey: "filters.registrations.jurisdictionPrefix",
  allOptionLabelKey: "filters.registrations.allJurisdictions",
  options: [],
});

const REGISTRATIONS_TYPE_OF_REGISTRATION_FILTER = Object.freeze({
  id: NOTIFICATION_CENTER_FILTER_IDS.REGISTRATIONS_TYPE_OF_REGISTRATION,
  type: "select",
  optionsSource: "types",
  reduxKey: "registrationsTypeOfRegistrationFilter",
  variant: "labeled",
  labelPrefixKey: "filters.registrations.typeOfRegistrationPrefix",
  placeholderKey: "filters.registrations.allTypesOfRegistration",
  ariaLabelKey: "filters.registrations.typeOfRegistration",
  allOptionLabelKey: "filters.registrations.allTypesOfRegistration",
  options: [],
});

const REGISTRATIONS_SUBCATEGORY_FILTER = Object.freeze({
  id: NOTIFICATION_CENTER_FILTER_IDS.REGISTRATIONS_SUBCATEGORY,
  type: "multiSelect",
  optionsSource: "subcategories",
  reduxKey: "registrationsSubcategoryFilter",
  placeholderKey: "filters.registrations.allSubcategories",
  ariaLabelKey: "filters.registrations.subcategory",
  labelPrefixKey: "filters.registrations.subcategoryPrefix",
  allOptionLabelKey: "filters.registrations.allSubcategories",
  visibleWhen: {
    reduxKey: "registrationsTypeOfRegistrationFilter",
    equals: "EPR",
  },
  options: [],
});

const REGISTRATIONS_STATUS_FILTER = Object.freeze({
  id: NOTIFICATION_CENTER_FILTER_IDS.REGISTRATIONS_STATUS,
  type: "multiSelect",
  optionsSource: "statuses",
  reduxKey: "registrationsStatusFilter",
  placeholderKey: "filters.registrations.allStatuses",
  ariaLabelKey: "filters.registrations.status",
  labelPrefixKey: "filters.registrations.statusPrefix",
  allOptionLabelKey: "filters.registrations.allStatuses",
  options: [],
});

const REGISTRATIONS_DATE_LAST_STATUS_CHANGED_FILTER = Object.freeze({
  id: NOTIFICATION_CENTER_FILTER_IDS.REGISTRATIONS_DATE_LAST_STATUS_CHANGED,
  type: "dateRange",
  reduxKey: "registrationsDateLastStatusChangedFilter",
  placeholderKey: "filters.registrations.dateLastStatusChanged",
  ariaLabelKey: "filters.registrations.dateLastStatusChanged",
  labelPrefixKey: "filters.registrations.dateLastStatusChangedPrefix",
  orderingField: "date_last_status_changed",
  options: [],
});

const REGISTRATIONS_REGISTRATOR_FILTER = Object.freeze({
  id: NOTIFICATION_CENTER_FILTER_IDS.REGISTRATIONS_REGISTRATOR,
  type: "multiSelect",
  optionsSource: "registrators",
  reduxKey: "registrationsRegistratorFilter",
  placeholderKey: "filters.registrations.allRegistrators",
  ariaLabelKey: "filters.registrations.registrator",
  labelPrefixKey: "filters.registrations.registratorPrefix",
  allOptionLabelKey: "filters.registrations.allRegistrators",
  options: [],
});

const REGISTRATIONS_ESTIMATED_REGISTRATION_DATE_FILTER = Object.freeze({
  id: NOTIFICATION_CENTER_FILTER_IDS.REGISTRATIONS_ESTIMATED_REGISTRATION_DATE,
  type: "dateRange",
  reduxKey: "registrationsEstimatedRegistrationDateFilter",
  placeholderKey: "filters.registrations.estimatedRegistrationDate",
  ariaLabelKey: "filters.registrations.estimatedRegistrationDate",
  labelPrefixKey: "filters.registrations.estimatedRegistrationDatePrefix",
  orderingField: "estimated_date_of_registration",
  options: [],
});

/** @type {Record<string, NotificationCenterFilterDef>} */
export const NOTIFICATION_CENTER_FILTER_DEFINITIONS = Object.freeze({
  [NOTIFICATION_CENTER_FILTER_IDS.VISIBILITY]: VISIBILITY_FILTER,
  [NOTIFICATION_CENTER_FILTER_IDS.DEADLINES_SCOPE]: DEADLINES_SCOPE_FILTER,
  [NOTIFICATION_CENTER_FILTER_IDS.DECLARATION_TYPE]: DECLARATION_TYPE_FILTER,
  [NOTIFICATION_CENTER_FILTER_IDS.REGISTRATIONS_COMPANY]:
    REGISTRATIONS_COMPANY_FILTER,
  [NOTIFICATION_CENTER_FILTER_IDS.REGISTRATIONS_ORDER_DATE]:
    REGISTRATIONS_ORDER_DATE_FILTER,
  [NOTIFICATION_CENTER_FILTER_IDS.REGISTRATIONS_JURISDICTION]:
    REGISTRATIONS_JURISDICTION_FILTER,
  [NOTIFICATION_CENTER_FILTER_IDS.REGISTRATIONS_TYPE_OF_REGISTRATION]:
    REGISTRATIONS_TYPE_OF_REGISTRATION_FILTER,
  [NOTIFICATION_CENTER_FILTER_IDS.REGISTRATIONS_SUBCATEGORY]:
    REGISTRATIONS_SUBCATEGORY_FILTER,
  [NOTIFICATION_CENTER_FILTER_IDS.REGISTRATIONS_STATUS]:
    REGISTRATIONS_STATUS_FILTER,
  [NOTIFICATION_CENTER_FILTER_IDS.REGISTRATIONS_DATE_LAST_STATUS_CHANGED]:
    REGISTRATIONS_DATE_LAST_STATUS_CHANGED_FILTER,
  [NOTIFICATION_CENTER_FILTER_IDS.REGISTRATIONS_REGISTRATOR]:
    REGISTRATIONS_REGISTRATOR_FILTER,
  [NOTIFICATION_CENTER_FILTER_IDS.REGISTRATIONS_ESTIMATED_REGISTRATION_DATE]:
    REGISTRATIONS_ESTIMATED_REGISTRATION_DATE_FILTER,
});

export const NOTIFICATION_CENTER_FILTER_DEFAULTS = Object.freeze({
  visibilityFilter: NOTIFICATION_CENTER_VISIBILITY.MINE,
  deadlinesScopeFilter: NOTIFICATION_CENTER_DEADLINES_SCOPE.NEXT_3_DAYS,
  declarationTypeFilter: NOTIFICATION_CENTER_DECLARATION_TYPE.ALL,
  registrationsCompanyFilter: null,
  registrationsOrderDateFilter: null,
  registrationsJurisdictionFilter: null,
  registrationsTypeOfRegistrationFilter: null,
  registrationsSubcategoryFilter: null,
  registrationsStatusFilter: getNotificationCenterRegistrationsDefaultStatuses(),
  registrationsDateLastStatusChangedFilter: null,
  registrationsRegistratorFilter: null,
  registrationsEstimatedRegistrationDateFilter: null,
  registrationsOrdering: null,
});

function normalizeFilterValue(def, raw) {
  const value = raw?.[def.reduxKey];

  switch (def.type) {
    case "multiSelect":
      if (def.reduxKey === "registrationsStatusFilter" && value === undefined) {
        return getNotificationCenterRegistrationsDefaultStatuses();
      }
      return normalizeRegistrationsJurisdictionFilter(value);
    case "dateRange":
      return normalizeRegistrationsOrderDateFilter(value);
    default: {
      if (Array.isArray(def.options) && def.options.length > 0) {
        const allowed = def.options.map((option) => option.value);
        return allowed.includes(value)
          ? value
          : NOTIFICATION_CENTER_FILTER_DEFAULTS[def.reduxKey];
      }
      return normalizeRegistrationsCompanyFilter(value);
    }
  }
}

/**
 * @param {Record<string, unknown>} [raw]
 */
export function normalizeNotificationCenterFilterState(raw) {
  const normalized = { ...NOTIFICATION_CENTER_FILTER_DEFAULTS };

  for (const def of Object.values(NOTIFICATION_CENTER_FILTER_DEFINITIONS)) {
    normalized[def.reduxKey] = normalizeFilterValue(def, raw);
  }

  normalized.registrationsOrdering = normalizeRegistrationsOrdering(
    raw?.registrationsOrdering
  );

  return normalized;
}

/**
 * Persist visibility only. Tab-specific scopes reset to product defaults each session.
 * @param {Record<string, unknown>} state
 */
export function pickNotificationCenterPersistedFilters(state) {
  const normalized = normalizeNotificationCenterFilterState(state);
  return {
    visibilityFilter: normalized.visibilityFilter,
  };
}

/**
 * @param {string | null | undefined} tabId
 */
export function getDefaultFiltersForTab(tabId) {
  const defs = getNotificationCenterFiltersForTab(tabId);
  const defaults = {};

  for (const def of defs) {
    defaults[def.reduxKey] = NOTIFICATION_CENTER_FILTER_DEFAULTS[def.reduxKey];
  }

  if (tabId === NOTIFICATION_CENTER_TAB_IDS.REGISTRATIONS) {
    defaults.registrationsOrdering =
      NOTIFICATION_CENTER_FILTER_DEFAULTS.registrationsOrdering;
  }

  return defaults;
}

/**
 * @param {string | null | undefined} tabId
 * @param {Record<string, unknown>} [raw]
 */
export function normalizeNotificationCenterFilterStateForTab(tabId, raw) {
  const normalized = getDefaultFiltersForTab(tabId);
  const global = normalizeNotificationCenterFilterState(raw);

  for (const def of getNotificationCenterFiltersForTab(tabId)) {
    if (global[def.reduxKey] !== undefined) {
      normalized[def.reduxKey] = global[def.reduxKey];
    }
  }

  if (tabId === NOTIFICATION_CENTER_TAB_IDS.REGISTRATIONS) {
    normalized.registrationsOrdering = global.registrationsOrdering ?? null;
  }

  return normalized;
}

const DEFAULT_TAB_FILTER_IDS = [NOTIFICATION_CENTER_FILTER_IDS.VISIBILITY];

/**
 * Filter ids shown per tab. Extend this map when a tab needs custom filters.
 * @type {Record<string, string[]>}
 */
export const NOTIFICATION_CENTER_TAB_FILTER_IDS = Object.freeze({
  [NOTIFICATION_CENTER_TAB_IDS.REPORTING]: [
    NOTIFICATION_CENTER_FILTER_IDS.VISIBILITY,
    NOTIFICATION_CENTER_FILTER_IDS.DECLARATION_TYPE,
  ],
  [NOTIFICATION_CENTER_TAB_IDS.MESSAGES]: DEFAULT_TAB_FILTER_IDS,
  [NOTIFICATION_CENTER_TAB_IDS.CASES]: DEFAULT_TAB_FILTER_IDS,
  [NOTIFICATION_CENTER_TAB_IDS.CHARGE_INVOICES]: DEFAULT_TAB_FILTER_IDS,
  [NOTIFICATION_CENTER_TAB_IDS.FUND_REQUESTS]: DEFAULT_TAB_FILTER_IDS,
  [NOTIFICATION_CENTER_TAB_IDS.PRODUCT]: DEFAULT_TAB_FILTER_IDS,
  [NOTIFICATION_CENTER_TAB_IDS.SETTINGS]: DEFAULT_TAB_FILTER_IDS,
  [NOTIFICATION_CENTER_TAB_IDS.REGISTRATIONS]: [
    NOTIFICATION_CENTER_FILTER_IDS.VISIBILITY,
    NOTIFICATION_CENTER_FILTER_IDS.REGISTRATIONS_COMPANY,
    NOTIFICATION_CENTER_FILTER_IDS.REGISTRATIONS_ORDER_DATE,
    NOTIFICATION_CENTER_FILTER_IDS.REGISTRATIONS_JURISDICTION,
    NOTIFICATION_CENTER_FILTER_IDS.REGISTRATIONS_TYPE_OF_REGISTRATION,
    NOTIFICATION_CENTER_FILTER_IDS.REGISTRATIONS_SUBCATEGORY,
    NOTIFICATION_CENTER_FILTER_IDS.REGISTRATIONS_STATUS,
    NOTIFICATION_CENTER_FILTER_IDS.REGISTRATIONS_DATE_LAST_STATUS_CHANGED,
    NOTIFICATION_CENTER_FILTER_IDS.REGISTRATIONS_REGISTRATOR,
    NOTIFICATION_CENTER_FILTER_IDS.REGISTRATIONS_ESTIMATED_REGISTRATION_DATE,
  ],
  [NOTIFICATION_CENTER_TAB_IDS.DEADLINES]: [
    NOTIFICATION_CENTER_FILTER_IDS.VISIBILITY,
    NOTIFICATION_CENTER_FILTER_IDS.DEADLINES_SCOPE,
  ],
});

/**
 * @param {string | null | undefined} tabId
 * @returns {NotificationCenterFilterDef[]}
 */
export function getNotificationCenterFiltersForTab(tabId) {
  const filterIds = NOTIFICATION_CENTER_TAB_FILTER_IDS[tabId] ?? DEFAULT_TAB_FILTER_IDS;
  return filterIds
    .map((id) => NOTIFICATION_CENTER_FILTER_DEFINITIONS[id])
    .filter(Boolean);
}

/**
 * @param {Record<string, unknown>} notificationCenterState
 * @param {string | null | undefined} tabId
 */
export function getNotificationCenterFilterValues(notificationCenterState, tabId) {
  return normalizeNotificationCenterFilterStateForTab(tabId, notificationCenterState);
}
